type ImageOptions = {
  width?: number;
  height?: number;

  /**
   * Aspect Ratio:
   * https://cloudinary.com/documentation/transformation_reference#ar_aspect_ratio
   *
   * Either:
   * - A string value in the form a:b, where a is the width and b is the height
   *   (e.g., 16:9).
   * - A decimal value representing the width divided by the height (e.g., 0.5).
   */
  ar?: `${number}:${number}` | number;

  /**
   * Gravity:
   * https://cloudinary.com/documentation/transformation_reference#g_gravity
   */
  g?: "auto";

  /**
   * Quality
   * https://cloudinary.com/documentation/transformation_reference#q_quality
   */
  q?: number | "auto";
};

export const getRbImageUrl = (
  media: string,
  options: ImageOptions = { width: 1000 },
): string => {
  /**
   * CREPO image URLs come in two forms:
   * 1. Transforms already applied, eg:
   *  https://img.redbull.com/images/c_crop,w_5464,h_3643,x_0,y_1161,f_auto,q_auto/c_scale,w_150/redbullcom/2022/5/5/esxtfazwc5k0xntwv20i/max-verstappen-profile-pic
   * 2. With a placeholder ("{op}") for transforms to be passed, eg:
   *  https://img.redbull.com/images/{op}/redbullcom/2022/5/5/esxtfazwc5k0xntwv20i/max-verstappen-profile-pic
   *  https://files.speedweek.com/images/{op}/2026/8/18/jocg7c91y2cxhwdzq3oh/gn-g_1330335_hi-res
   *
   * This function needs to be able to handle both formats and different CREPO domains.
   */
  const transform = getImageUrlTransform(options);

  if (media.includes("{op}")) {
    return media.replace("{op}", transform);
  }

  return media.replace(/(\/images\/).+(\/redbullcom\/)/, `$1${transform}$2`);
};

function getImageUrlTransform({
  height,
  width,
  ar,
  g,
  q = "auto",
}: ImageOptions) {
  /**
   * See:
   * https://cloudinary.com/documentation/transformation_reference
   */
  const options = ["c_fill", "f_auto"];

  if (q) {
    options.push(`q_${q}`);
  }

  if (width) {
    options.push(`w_${width}`);
  }
  if (height) {
    options.push(`h_${height}`);
  }
  if (width && height) {
    options.push("g_auto");
  }
  if (g) {
    options.push(`g_${g}`);
  }
  if (ar) {
    options.push(`ar_${ar}`);
  }

  return options.join(",");
}
